import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import BaseHighchart from '../BaseHighchart';
import prepareData from './builder';
import { ReduxState } from '../../../types';
import ChartFilter from '../ChartFilter';
import { navigateToChart } from '../../../utils/helpers';
import { TimePointSchema, ConversationSchema } from '../../../requests/api/apiTypes';
import ConversationModal from '../ConversationTab/conversationModal';
import { ChartFiltersWeather, ChartDataType } from '../types';
import { ET0_NAME, CHART_TITLE_ETO } from '../../../constants/graphs';
import apiClient from '../../../requests/api/apiClient';
import { FetchError } from '../../../views/types';
import { getConversationTooltip } from '../LineCharts/utils';
import {
  getPointWidth, getDayDomain, anchorPointsAtNoon, getDayPlotLines, MAX_POINT_WIDTH,
} from '../utils';
import ChartConfigDialog from '../ChartConfigDialog/ChartConfigDialog';
import Et0ConfigContent from './Et0ConfigContent';
import useChartConfigClick from '../../../hooks/useChartConfigClick';

dayjs.extend(utc);

// To Do: Actualizar types
// To Do: Actualizar types
function Et0Chart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideYValues = false,
  hideXValues = false,
  height = '300',
  title = CHART_TITLE_ETO,
  yAxisTitle = '',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  isWeatherStation,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideYValues?: boolean
  hideXValues?: boolean
  height?: string
  title?: string
  yAxisTitle?: string
  isWeatherStation?: boolean
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
}) {
  const chartName = 'et0';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const preparedFilters: ChartFiltersWeather = useMemo(() => {
    if (!interval) {
      return {
        farmId,
        startDate: '',
        endDate: '',
        weatherZoneId: '',
        sn: '',
      };
    }
    return {
      farmId,
      sectorId,
      startDate: interval.startDate,
      endDate: interval.endDate,
      weatherZoneId: selectedSector?.weatherZoneId,
      telemetryType: selectedSector?.telemetryType,
      sn: selectedSector?.sn,
      isWeatherStation,
    };
  }, [farmId, interval, selectedSector?.weatherZoneId, isWeatherStation]);

  const columnWidth = useMemo(() => {
    if (!interval?.startDate || !interval?.endDate) return MAX_POINT_WIDTH;
    return getPointWidth(interval.startDate, interval.endDate);
  }, [interval]);

  const createSeriesData = useCallback((preparedData: any) => [
    {
      type: 'column',
      name: 'ETo (mm)',
      data: anchorPointsAtNoon(preparedData.et0),
      yAxis: 0,
      color: '#FEBF23',
      pointWidth: columnWidth,
      pointPlacement: 'on',
    },
    {
      type: 'line',
      name: 'ETo acumulado (mm)',
      data: anchorPointsAtNoon(preparedData.cumulativeEt0),
      yAxis: 0,
      visible: false,
      pointPlacement: 'on',
    },
  ], [columnWidth]);

  // To Do: Actualizar types
  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      if (interval) {
        const preparedData = await prepareData(preparedFilters, selectedSector?.telemetryType);
        const seriesData = createSeriesData(preparedData);

        setChartData({
          series: seriesData,
          categories: preparedData.categories,
          absoluteCategories: preparedData.absoluteCategories as string[],
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [preparedFilters, interval]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      if (this.series.name === 'Comentarios') {
        return getConversationTooltip(this, conversations);
      }
      const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
      const subHeader = `<div style="font-size: 12px; margin-bottom: 8px;">${dayjs(this.x).format('MMM DD, YYYY')}</br></div>`;
      const points = this.points?.map(
        (point) => `<div style="margin: 4px 0"><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${Number(point.y).toFixed(2)}</b></div>`,
      ).join('');
      return `<div style="padding: 8px">${header}${subHeader}${points}</div>`;
    },
  }), []);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            ET0_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  // Shared day domain (getDayDomain) so ETo aligns with the irrigation/roots charts.
  const { min: xMin, max: xMax } = useMemo(
    () => getDayDomain(interval?.startDate, interval?.endDate), [interval],
  );

  const dayLines = useMemo(() => getDayPlotLines(xMin, xMax), [xMin, xMax]);

  return (
    <>
      <Box>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={title}
            hideYValues={hideYValues}
            interval={interval}
            categories={chartData.categories}
            yAxisTitle={yAxisTitle}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            tooltip={customTooltip}
            chartConfig={{
              chart: {
                height,
                type: 'column',
              },
              xAxis: {
                type: 'datetime',
                min: xMin,
                max: xMax,
                labels: {
                  format: '{value:%b %d}',
                  enabled: !hideXValues,
                },
                plotLines: dayLines,
              },
              yAxis:
              {
                min: 0,
                title: {
                  text: yAxisTitle || null,
                },
              },
              plotOptions: {
                column: {
                  pointPadding: 0,
                  borderWidth: 0,
                },
              },
            }}
            setModalState={setModalState}
            conversations={conversations}
            absoluteCategories={chartData.absoluteCategories}
            legend={{ enabled: !hideLegend }}
            hideXValues={hideXValues}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            zoomSync={true}
            autoAdjustYAxis={true}
          />
        </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={ET0_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · ETo"
      >
        <Et0ConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </>
  );
}

export default Et0Chart;
